import { useEffect, useMemo, useState } from 'react';
import { setMilliseconds, sub } from 'date-fns';
import styled from 'styled-components';

import FilterBar from 'components/shared/filters/FilterBar';
import FilterMultiSelect from 'components/shared/filters/FilterMultiSelect';
import DatePicker from 'components/shared/form/DatePicker';
import { capitalize } from 'lib/util';
import { ReviewStatus } from 'models/Review';
import { indexReviews } from 'services/api/reviews';
import { ReferralsFilterState as Filters, useReferralsActions, useReferralsStore } from 'stores/referralsStore';

import DatePickerShortcuts from '../insights/filterBar/DatePickerShortcuts';

import { getFilterSections } from './helpers/filters';

interface ReferralsFilterBarProps {
  onApplyFilters: (filters: { search: string; [key: string]: any }) => void;
}

const now = setMilliseconds(new Date(), 0);
const thirtyDaysAgo = sub(now, { days: 30 });

function ReferralsFilterBar({ onApplyFilters }: ReferralsFilterBarProps) {
  const filters = useReferralsStore((state) => state.filters);
  const { setFilters } = useReferralsActions();

  // We track the filter dates separately from the selected datepicker values so that we can delay
  // updating the filter state until the datepicker closes. Otherwise, if the user is updating date part
  // values in the datepicker via the keyboard, we'd send an API request for every character typed in.
  const createdAtFilterValue = filters.createdAt as unknown as Date[] | undefined;
  const startDateFilterValue = createdAtFilterValue?.[0] || thirtyDaysAgo;
  const endDateFilterValue = createdAtFilterValue?.[1] || now;
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(startDateFilterValue);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(endDateFilterValue);
  const [filteredStartDate, setFilteredStartDate] = useState<Date | null>(startDateFilterValue);
  const [filteredEndDate, setFilteredEndDate] = useState<Date | null>(endDateFilterValue);
  const maxDate = useMemo(() => new Date(), []);

  const handleClearFilters = () => {
    setFilters({});
  };

  useEffect(() => {
    if (!filteredStartDate && !filteredEndDate) {
      const newFilters = { ...filters };
      delete newFilters.createdAt;
      setFilters(newFilters);
    } else {
      setFilters({
        ...filters,
        createdAt: [filteredStartDate || new Date('1900-01-01'), filteredEndDate || new Date('2999-01-01')] as any,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFilters, filteredStartDate, filteredEndDate]);

  const DatePickerFooter = ({ dateInput }) => (
    <DatePickerShortcuts
      datePickerRef={dateInput}
      setDates={([startDate, endDate]) => {
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        setFilteredStartDate(startDate);
        setFilteredEndDate(endDate);
      }}
    />
  );

  // Get filter sections internally
  const filterSections = useMemo(() => getFilterSections(), []);

  const handleStatusChange = (value) => {
    setFilters({ ...filters, status: value });
  };

  return (
    <FilterBar
      applyFilters={(filters: Filters) => {
        onApplyFilters({ search: '', ...filters });
      }}
      filters={filters}
      filterSections={filterSections}
      onClearFilters={handleClearFilters}
      title='Referrals'
      resultsText='referrals'
      queryFn={(inp, signal) => indexReviews({ ...inp, filters: { ...inp.filters, type: 'referral' } }, signal)}
      quickFilterShowMinWidth={1100}
      quickFilter={
        <FilterMultiSelect
          name='status'
          placeholder='Status'
          options={Object.values(ReviewStatus).map((status) => ({
            id: status,
            name: status === ReviewStatus.REJECTED ? 'Denied' : capitalize(status),
          }))}
          getOptionLabel={(obj) => obj?.name ?? ''}
          getOptionValue={(obj) => obj?.id ?? ''}
          value={filters.status || []}
          onChange={handleStatusChange}
        />
      }
      callToAction={
        <DatePickerContainer>
          <DatePicker
            data-gtm-id='referralsStartDateFilter'
            onCalendarClose={() => setFilteredStartDate(selectedStartDate)}
            onBlur={() => setFilteredStartDate(selectedStartDate)}
            onChange={(val) => setSelectedStartDate(val as Date)}
            selected={selectedStartDate}
            maxDate={selectedEndDate ?? undefined}
            renderFooter={DatePickerFooter}
            popperPlacement='bottom-start'
          />

          <Emdash>&mdash;</Emdash>

          <DatePicker
            data-gtm-id='referralsEndDateFilter'
            onCalendarClose={() => setFilteredEndDate(selectedEndDate)}
            onBlur={() => setFilteredEndDate(selectedEndDate)}
            onChange={(val) => setSelectedEndDate(val as Date)}
            selected={selectedEndDate}
            maxDate={maxDate}
            minDate={selectedStartDate ?? undefined}
            renderFooter={DatePickerFooter}
            popperPlacement='bottom-start'
          />
        </DatePickerContainer>
      }
    />
  );
}

export default ReferralsFilterBar;

const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;

  input {
    max-width: 160px;

    @media ${({ theme }) => theme.devices.desktop} {
      max-width: 140px;
    }
  }
`;

const Emdash = styled.span`
  margin: 0 8px;
`;
